import { graphql, Link, navigate, PageProps } from "gatsby";
import React, { Fragment, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import {
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import type { SERPPageContext } from "../../../gatsby-node";
import { OutboundLink } from "../../../plugins/gatsby-plugin-google-gtag";
import { ModalState } from "../../../plugins/gatsby-source-serp/types";
import AdSense from "../../components/adverts/AdSense";
import AdSenseScript from "../../components/adverts/AdSenseScript";
import IndeedWidget from "../../components/IndeedWidget";
import Layout from "../../components/Layout";
import useExitItemModal from "../../hooks/useExitIntent";
import type { AllowedSearchParams } from "../../hooks/useFormValuesFromURL";
import useFormValuesFromURL from "../../hooks/useFormValuesFromURL";
import useJobsQuery, { Source } from "../../hooks/useJobsQuery";
import { trackCustomEvent } from "../../utils/analytics";
import getNavigatePath from "../../utils/getNavigatePath";
import ExitIntentJobModal from "./ExitIntentJobModal";
import Modal from "./Modal";
import ModalDCForm from "./ModalDCForm";
import ModalDCFormForJobClick from "./ModalDCFormForJobClick";
import ModalJobSuggestion from "./ModalJobSuggestion";
import Pagination from "./Pagination";
import SearchForm from "./SearchForm";
import SerpJobLink from "./SerpJobLink";

const GATSBY_HAS_SERP_BG = process.env.GATSBY_HAS_SERP_BG === "true";
const GATSBY_COUNTRY = process.env.GATSBY_COUNTRY;

const indeedWidgetContainerId = "indeed-widget-serp";

// TODO Move to shared
export type FormValues = {
  position: string;
  location: string;
  start?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: "bing" | "google" | "JobSites";
  utm_term?: string;
  lscid?: string;
  clickid?: string;
  gclid?: string;
  locphysical?: string;
  email?: string;
  firstname?: string;
  lastname?: string;
  phone?: string;
  compare?: boolean;
  postCode?: string;
};

function getInitialModalState(onPageLoad, jbeModal) {
  if (onPageLoad === "jobModal") {
    return ModalState.job;
  }
  return jbeModal;
}

function getTitle(tx: any, values) {
  if (values.location && values.position) {
    return tx!.titleWithLocation
      .replace("{{location}}", values.location)
      .replace("{{position}}", values.position);
  }
  if (values.position) {
    return tx!.title.replace("{{position}}", values.position);
  }
  return tx!.titleWithoutPosition.replace("{{position}}", values.position);
}

function TopAdSlot({
  adSenseSlots,
  offset,
  values,
  hasIndeedWidget,
  hideGoogleAds,
}) {
  if (hasIndeedWidget) {
    return (
      <IndeedWidget
        indeedWidgetContainerId={indeedWidgetContainerId}
        eventCategory="SERP"
        position={values.position}
        location={values.location}
      />
    );
  }

  if (hideGoogleAds) {
    return null;
  }

  return (
    <Fragment key={`top-${offset}`}>
      <AdSense
        adSenseSlots={adSenseSlots}
        position="top"
        utmSource={values.utm_source}
      />
    </Fragment>
  );
}

// export const Head = ({
//   data: {
//     serp: {
//       config: { hasExitIntent },
//     },
//   },
// }) => {
//   if (hasExitIntent) return <HotJar />;
//   return null;
// };

export default function Serp(
  props: PageProps<Queries.SERPPageQuery, SERPPageContext>
) {
  const {
    data: {
      adSenseSlots,
      footerTx,
      cookieBannerTx,
      tx,
      cpaModalTx,
      hostedApplyTx,
      site,
      serp: {
        compareWithConfig,
        config: {
          numberOfJobsToCall,
          showJobModalOnJbeCloseAndSubmit,
          jbeModal,
          onPageLoad,
          onSERPJobClick,
          positionOverride,
          hideGoogleAds,
          hasIndeedWidget,
          serpPathWithoutModal,
          serpPathWithJobModal,
          googleOneTap,
          googleSignIn,
          hasExitIntent,
          withRgDirect,
        },
      },
    },
    pageContext,
    location,
  } = props;
  const { jobQuery } = pageContext;
  const { register, handleSubmit, reset, getValues } = useForm<FormValues>({
    mode: "onBlur",
  });
  const values = getValues();

  useFormValuesFromURL(location, (searchParams: AllowedSearchParams) => {
    if (jbeModal === "email") {
      const { phone, ...searchParamsWithoutPhone } = searchParams;
      reset(searchParamsWithoutPhone);
    } else {
      reset(searchParams);
    }
  });

  const { addExitIntentHash, exitItentActive } = useExitItemModal(
    location.hash,
    hasExitIntent
  );

  function onSubmit(data: FormValues) {
    const path = getNavigatePath(data, location.pathname);
    navigate(path);
  }

  const initialModalState = getInitialModalState(onPageLoad, jbeModal);
  const [modalState, _toggleModalState] =
    useState<ModalState>(initialModalState);

  const modalStateRef = useRef(modalState);
  function toggleModalState(modalState: ModalState | null) {
    modalStateRef.current = modalState;
    if (modalState === null) {
      addExitIntentHash?.();
      trackCustomEvent({
        category: "SERP",
        action: `Modal close`,
        label: location.pathname,
      });
    }
    _toggleModalState(modalState);
  }

  const [requestedJob, setRequestedJob] = useState("");

  // Used in pagination
  const [offset, setOffset] = useState<number>(jobQuery.offset);
  const { status, data, isLoading } = useJobsQuery(
    {
      ...jobQuery,
      ...values,
      offset,
      source: Source.serp,
      withRgDirect,
    },
    positionOverride
  );

  return (
    <Layout
      location={location}
      title={getTitle(tx, values)}
      footerTx={footerTx}
      cookieBannerTx={cookieBannerTx}
      className="relative mx-auto w-full"
      pageContext={pageContext}
      site={site}
      positionOverride={positionOverride}
      enableHotJar={hasExitIntent}
      noindex
      HeaderLink={
        tx!.postJobLink && tx!.postJobText ? (
          <div className="ml-auto flex divide-x-2">
            {GATSBY_COUNTRY === "us" &&
              tx.jobsInEducationLink &&
              tx.jobsInEducationText && (
                <Link
                  to={tx.jobsInEducationLink}
                  className="px-2 text-xs text-primary-600 hover:text-primary-900 sm:block sm:text-base"
                >
                  {tx!.jobsInEducationText}
                </Link>
              )}
            {tx.postJobLink && tx.postJobText && (
              <OutboundLink
                className="hidden px-2 text-base text-primary-600 hover:text-primary-900 sm:block"
                href={tx!.postJobLink!}
                target="_blank"
              >
                {tx!.postJobText}
              </OutboundLink>
            )}
          </div>
        ) : null
      }
    >
      {GATSBY_HAS_SERP_BG && (
        <div className="fixed inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-3xl">
          <svg
            className="relative left-[calc(50%-11rem)] -z-10 h-[21.1875rem] max-w-none -translate-x-1/2 rotate-[30deg] sm:left-[calc(50%-30rem)] sm:h-[42.375rem]"
            viewBox="0 0 1155 678"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="url(#45de2b6b-92d5-4d68-a6a0-9b9b2abad533)"
              fillOpacity="0.4"
              d="M317.219 518.975L203.852 678 0 438.341l317.219 80.634 204.172-286.402c1.307 132.337 45.083 346.658 209.733 145.248C936.936 126.058 882.053-94.234 1031.02 41.331c119.18 108.451 130.68 295.337 121.53 375.223L855 299l21.173 362.054-558.954-142.079z"
            />
            <defs>
              <linearGradient
                id="45de2b6b-92d5-4d68-a6a0-9b9b2abad533"
                x1="1155.49"
                x2="-78.208"
                y1=".177"
                y2="474.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#9089FC" />
                <stop offset={1} stopColor="#FF80B5" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      )}
      {exitItentActive && data && data.length && (
        <ExitIntentJobModal
          data={data}
          tx={tx}
          cpaModalTx={cpaModalTx}
          hostedApplyTx={hostedApplyTx}
          numberOfJobsToCall={numberOfJobsToCall}
        />
      )}
      <SearchForm
        location={location}
        tx={tx}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        register={register}
        values={values}
      />
      <div className="relative overflow-hidden border-t border-gray-200">
        {isLoading && (
          <div className="flex h-[30rem] flex-col items-center justify-center">
            <div className="loader relative h-20 w-20" />
          </div>
        )}
        {status === "error" && (
          <div className="flex items-start justify-center rounded-md bg-red-50 p-12">
            <XCircleIcon
              className="h-7 w-7 text-yellow-400"
              aria-hidden="true"
            />
            <h3 className="text-md ml-3 font-medium text-yellow-800">
              {tx!.error}
            </h3>
          </div>
        )}
        {data && data.length === 0 ? (
          <div className="flex items-start justify-center rounded-md bg-yellow-50 p-12">
            <ExclamationTriangleIcon
              className="h-7 w-7 text-yellow-400"
              aria-hidden="true"
            />
            <h3 className="text-md ml-3 font-medium text-yellow-800">
              {tx!.noJobsFound}
            </h3>
          </div>
        ) : null}
        {data && data.length ? (
          <>
            <TopAdSlot
              adSenseSlots={adSenseSlots}
              offset={offset}
              values={values}
              hasIndeedWidget={hasIndeedWidget}
              hideGoogleAds={hideGoogleAds}
            />
            <h2 className="sr-only">Jobs</h2>
            <ul>
              {data
                .slice(0, numberOfJobsToCall)
                .map((job: any, index: number) => {
                  const isMidway = Math.floor(numberOfJobsToCall / 2) === index;

                  return (
                    <Fragment key={job.url}>
                      {!hideGoogleAds && isMidway ? (
                        <li
                          className="mx-auto my-8 max-w-xl px-4 lg:my-8 lg:max-w-3xl"
                          key="AdSense-mid"
                        >
                          <AdSense
                            key={`midway-${offset}`}
                            adSenseSlots={adSenseSlots}
                            position="mid"
                            utmSource={values.utm_source}
                          />
                        </li>
                      ) : null}
                      <li className="mx-auto my-8 max-w-xl px-4 lg:my-8 lg:max-w-3xl">
                        <SerpJobLink
                          job={job}
                          tx={tx}
                          cpaModalTx={cpaModalTx}
                          hostedApplyTx={hostedApplyTx}
                          onSERPJobClick={onSERPJobClick}
                          toggleModalState={toggleModalState}
                          jbeModal={jbeModal}
                          setRequestedJob={setRequestedJob}
                        />
                      </li>
                    </Fragment>
                  );
                })}
            </ul>
            {status === "success" && (
              <Fragment key={`bottom-${offset}`}>
                {hideGoogleAds ? null : (
                  <>
                    <AdSense
                      adSenseSlots={adSenseSlots}
                      position="bottom"
                      utmSource={values.utm_source}
                    />
                  </>
                )}
                <Pagination
                  offset={offset}
                  setOffset={setOffset}
                  numberOfJobsToCall={numberOfJobsToCall}
                  data={data}
                  tx={tx}
                />
              </Fragment>
            )}
          </>
        ) : null}
      </div>
      {values.position && (
        <Modal
          modalState={modalState}
          toggleModalState={toggleModalState}
          data={data}
        >
          <>
            {modalState === ModalState.job && (
              <ModalJobSuggestion
                cpaModalTx={cpaModalTx}
                hostedApplyTx={hostedApplyTx}
                tx={tx}
                toggleModalState={toggleModalState}
                location={location}
                data={data}
              />
            )}
            {modalState === ModalState.email ||
            modalState === ModalState["email,sms"] ? (
              <ModalDCForm
                tx={tx}
                toggleModalState={toggleModalState}
                jbeModal={jbeModal}
                values={values}
                compareWithConfig={
                  googleOneTap || googleSignIn ? null : compareWithConfig // disable compare with if googleOneTap or googleSignIn is enabled
                }
                serpPathWithoutModal={serpPathWithoutModal}
                serpPathWithJobModal={serpPathWithJobModal}
                googleOneTap={googleOneTap}
                googleSignIn={googleSignIn}
                showJobModalOnJbeCloseAndSubmit={
                  showJobModalOnJbeCloseAndSubmit
                }
                location={location}
                addExitIntentHash={addExitIntentHash}
              />
            ) : null}
            {modalState === ModalState["email,redirectToJob"] ? (
              <ModalDCFormForJobClick
                tx={tx}
                toggleModalState={toggleModalState}
                jbeModal={jbeModal}
                values={values}
                compareWithConfig={compareWithConfig}
                serpPathWithoutModal={serpPathWithoutModal}
                showJobModalOnJbeCloseAndSubmit={
                  showJobModalOnJbeCloseAndSubmit
                }
                location={location}
                onSubmit={onSubmit}
                requestedJob={requestedJob}
                modalState={modalState}
              />
            ) : null}
          </>
        </Modal>
      )}
      {data && data.length && !hideGoogleAds ? <AdSenseScript /> : null}

      {GATSBY_HAS_SERP_BG && (
        <div className="fixed inset-x-0 bottom-0 -z-10 transform-gpu overflow-hidden blur-3xl">
          <svg
            className="relative left-[calc(50%+3rem)] h-[21.1875rem] max-w-none -translate-x-1/2 sm:left-[calc(50%+36rem)] sm:h-[42.375rem]"
            viewBox="0 0 1155 678"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="url(#ecb5b0c9-546c-4772-8c71-4d3f06d544bc)"
              fillOpacity=".3"
              d="M317.219 518.975L203.852 678 0 438.341l317.219 80.634 204.172-286.402c1.307 132.337 45.083 346.658 209.733 145.248C936.936 126.058 882.053-94.234 1031.02 41.331c119.18 108.451 130.68 295.337 121.53 375.223L855 299l21.173 362.054-558.954-142.079z"
            />
            <defs>
              <linearGradient
                id="ecb5b0c9-546c-4772-8c71-4d3f06d544bc"
                x1="1155.49"
                x2="-78.208"
                y1=".177"
                y2="474.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#9089FC" />
                <stop offset={1} stopColor="#FF80B5" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      )}
    </Layout>
  );
}

export const query = graphql`
  query SERPPage($id: String!) {
    serp(id: { eq: $id }) {
      compareWithConfig {
        partner
        url
      }
      config {
        onPageLoad
        onSERPJobClick
        jbeModal
        showJobModalOnJbeCloseAndSubmit
        numberOfJobsToCall
        hideGoogleAds
        pushnami
        serpPathWithoutModal
        serpPathWithJobModal
        googleOneTap
        googleSignIn
        hasExitIntent
        hasIndeedWidget
        withRgDirect
        positionOverride {
          positions
          jobSrc
          querySrc
        }
      }
    }
    adSenseSlots: adSense {
      ...AdSenseSlots
    }
    tx: translations(id: { eq: "SERP" }) {
      title
      titleWithLocation
      titleWithoutPosition
      positionLabel
      required
      locationLabel
      continueLabel
      error
      noJobsFound
      viewJob
      found
      jobs
      in
      postedXago
      collectionTitle
      collectionSubtitle
      collectionEmailLabel
      collectionEmailPlaceholder
      collectionEmailInvalid
      collectionPhonePlaceholder
      collectionPhoneInvalid
      collectionActivate
      collectionActivateAndViewJob
      collectionSkipAndViewJob
      collectionDisclaimerEmailSMS
      collectionDisclaimerEmail
      collectionCompareWith
      postJobLink
      postJobText
      jobsInEducationLink
      jobsInEducationText
      next
      previous
      exitIntentTitle
      exitIntentTitlePlural
    }
    footerTx: translations(id: { eq: "Footer" }) {
      ...Footer
    }
    cookieBannerTx: translations(id: { eq: "CookieBanner" }) {
      ...CookieBanner
    }
    cpaModalTx: translations(id: { eq: "CpaModal" }) {
      ...CpaModal
    }
    hostedApplyTx: translations(id: { eq: "HostedApply" }) {
      ...HostedApply
    }
    site {
      ...SiteMetadata
    }
  }
`;
